import React, { forwardRef } from 'react'
import { twMerge } from 'tailwind-merge'

import { Textarea as GsTextarea, TextareaInput as GsTextareaInput } from '../../_gluestack/textarea'
import { FieldFocusProvider, useFieldFocusState, useSetFieldFocused } from '../../fieldFocus'
import { useFieldInvalid } from '../../fieldInvalid'
import { MULTILINE_FIELD_TEXT_CLASS } from '../../fieldTypography'
import { webOnly } from '../../webOnly'

// Prop-driven AOS Textarea — owns the Figma styling (node 6981:11986) and wraps
// the vendored Gluestack compound (which keeps the data-[hover|invalid|disabled]
// interaction wiring). The `_gluestack/textarea` tva is neutralized to
// structure-only. Same form tokens as Input: bg/input, border/subtle default →
// border/strong hover → 2px action/focus-ring focus → 2px status/critical invalid →
// 40% opacity disabled. Field text is P1 16/24, top-aligned.

const textareaBase = twMerge(
    'w-full min-h-[100px] rounded bg-background-input p-3',
    'border border-border-subtle',
    'data-[hover=true]:border-border-strong',
    'data-[invalid=true]:border-2 data-[invalid=true]:border-status-critical',
    'data-[disabled=true]:opacity-40 data-[disabled=true]:border-border-subtle',
)

// Figma aliases Semantic/Focus-ring to Action/default, so the frame follows the
// follows the brand (blue on AOS, yellow on AOIMS/TLL).
const textareaFocused = 'border-2 border-focus-ring data-[hover=true]:border-focus-ring'

type GsTextareaRef = React.ComponentRef<typeof GsTextarea>
export type TextareaProps = Omit<
    React.ComponentPropsWithoutRef<typeof GsTextarea>,
    'variant' | 'size'
> & {
    className?: string
}

export const Textarea = forwardRef<GsTextareaRef, TextareaProps>(
    ({ className, isInvalid, ...rest }, ref) => {
        const { isFocused, setFocused } = useFieldFocusState()
        const fieldInvalid = useFieldInvalid()

        return (
            <FieldFocusProvider setFocused={setFocused}>
                <GsTextarea
                    ref={ref}
                    isInvalid={isInvalid ?? fieldInvalid}
                    className={twMerge(textareaBase, isFocused ? textareaFocused : '', className)}
                    {...rest}
                />
            </FieldFocusProvider>
        )
    },
)
Textarea.displayName = 'Textarea'

// Web resize: the grip needs `resize-y`, and the dragged height only sticks when
// the flex basis is `auto` (`flex-1` is basis 0%, which discards it).
const textareaInputBase = twMerge(
    'flex-1 p-0 font-regular',
    webOnly('flex-auto resize-y'),
    MULTILINE_FIELD_TEXT_CLASS,
    'text-content-default placeholder:text-content-muted',
)

type GsTextareaInputRef = React.ComponentRef<typeof GsTextareaInput>
export type TextareaInputProps = React.ComponentPropsWithoutRef<typeof GsTextareaInput> & {
    className?: string
}

export const TextareaInput = forwardRef<GsTextareaInputRef, TextareaInputProps>(
    ({ className, onFocus, onBlur, ...rest }, ref) => {
        const setFocused = useSetFieldFocused()

        return (
            <GsTextareaInput
                ref={ref}
                className={twMerge(textareaInputBase, className)}
                onFocus={event => {
                    setFocused?.(true)
                    onFocus?.(event)
                }}
                onBlur={event => {
                    setFocused?.(false)
                    onBlur?.(event)
                }}
                {...rest}
            />
        )
    },
)
TextareaInput.displayName = 'TextareaInput'
